import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import {
  Table,
  Card,
  Tag,
  Button,
  Spin,
  message,
  Input,
  Select,
  Space,
  Modal
} from 'antd'
import {
  LogoutOutlined,
  ReloadOutlined,
  SearchOutlined,
  EyeOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons'
import api from '../utils/api'
import { formatTz } from '../utils/timezone'

const { TextArea } = Input

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f5f0 0%, #e8e8e8 50%, #f0ede5 100%);
  padding: 20px;

  @media (max-width: 768px) {
    padding: 15px 10px;
  }
`

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 15px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`

const Title = styled.h1`
  font-size: 28px;
  color: #5a5a5a;
  margin: 0;
  font-weight: 500;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
  }
`

const StyledButton = styled(Button)`
  @media (max-width: 768px) {
    width: 100%;
  }
`

const FilterCard = styled(Card)`
  margin-bottom: 20px;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(154, 163, 175, 0.15);
  background: #faf9f6;
  border: 1px solid rgba(168, 181, 192, 0.2);

  .ant-card-body {
    padding: 20px;
  }
`

const FilterRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const FilterItem = styled.div`
  flex: 1;
  min-width: 200px;

  @media (max-width: 768px) {
    width: 100%;
  }
`

const StyledTable = styled(Table)`
  background: #faf9f6;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(154, 163, 175, 0.15);

  .ant-table {
    background: transparent;
  }

  .ant-table-thead > tr > th {
    background: #ffffff;
    border-bottom: 1px solid rgba(168, 181, 192, 0.2);
    color: #5a5a5a;
    font-weight: 500;
  }

  .ant-table-tbody > tr {
    background: #ffffff;
    transition: all 0.2s ease;

    &:hover {
      background: #f8f7f4;
    }
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid rgba(168, 181, 192, 0.1);
  }

  @media (max-width: 768px) {
    font-size: 12px;
  }
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`

// 自定义 Tag 组件 - 莫兰迪色系
const CustomTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 4px;
  font-size: 12px;
  border-radius: 8px;
  font-weight: 500;
  letter-spacing: 0.3px;
  transition: all 0.2s ease;
  border: 1px solid;

  ${(props) => {
    switch (props.variant) {
      case 'success':
        return `
          background: #e8f5e9;
          color: #5a8a5a;
          border-color: #c4d4c4;
        `
      case 'error':
        return `
          background: #fce4ec;
          color: #b85a6a;
          border-color: #e4c4d4;
        `
      case 'warning':
        return `
          background: #fff3e0;
          color: #b8956a;
          border-color: #e4d4c4;
        `
      default:
        return `
          background: #f5f5f0;
          color: #8a8a8a;
          border-color: #d4d4c4;
        `
    }
  }}

  @media (max-width: 768px) {
    font-size: 12px;
    padding: 2px 4px;
    gap: 2px;
    border-radius: 6px;
  }
`

const SubscriptionApplicationsPage = () => {
  const [applications, setApplications] = useState([])
  const [loading, setLoading] = useState(false)
  const [token, setToken] = useState(null)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  })
  const [filters, setFilters] = useState({
    status: undefined,
    lineId: '',
    purchaseOrderNo: ''
  })
  const [detailModalVisible, setDetailModalVisible] = useState(false)
  const [selectedApplication, setSelectedApplication] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    const savedToken = sessionStorage.getItem('subscription_view_token')
    if (!savedToken) {
      message.warning('請先登入')
      navigate('/subscription/login')
      return
    }
    setToken(savedToken)
    fetchApplications()
  }, [navigate])

  useEffect(() => {
    if (token) {
      fetchApplications()
    }
  }, [pagination.current, pagination.pageSize, filters])

  const fetchApplications = async () => {
    const currentToken =
      token || sessionStorage.getItem('subscription_view_token')
    if (!currentToken) {
      navigate('/subscription/login')
      return
    }

    try {
      setLoading(true)
      const params = {
        page: pagination.current,
        pageSize: pagination.pageSize
      }

      if (filters.status) params.status = filters.status
      if (filters.lineId) params.lineId = filters.lineId
      if (filters.purchaseOrderNo)
        params.purchaseOrderNo = filters.purchaseOrderNo

      const response = await api.get('/api/subscription/applications', {
        headers: {
          Authorization: `Bearer ${currentToken}`
        },
        params
      })

      if (response.data.success) {
        const data = response.data.data
        setApplications(data.applications || [])
        setPagination((prev) => ({
          ...prev,
          total: data.pagination?.total || 0
        }))
      } else {
        message.error(response.data.message || '獲取申請表單失敗')
      }
    } catch (error) {
      console.error('獲取申請表單錯誤:', error)

      if (error.response?.status === 401) {
        const errorMessage = error.response?.data?.message || ''
        if (errorMessage.includes('過期') || errorMessage.includes('無效')) {
          sessionStorage.removeItem('subscription_view_token')
          setToken(null)
          message.error('登入已過期，請重新登入')
          navigate('/subscription/login')
          return
        }
      }

      message.error(
        error.response?.data?.message || '獲取申請表單失敗，請稍後再試'
      )
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateValue) => {
    if (!dateValue) return '-'

    let date
    if (typeof dateValue === 'object' && dateValue !== null) {
      if (dateValue.$date) {
        date = new Date(dateValue.$date)
      } else if (dateValue instanceof Date) {
        date = dateValue
      } else {
        return '-'
      }
    } else if (typeof dateValue === 'string') {
      date = new Date(dateValue)
    } else {
      return '-'
    }

    if (isNaN(date.getTime())) {
      return dateValue.toString()
    }

    // toLocaleString('zh-TW') 只設語系、不設時區，值仍跟著裝置跑
    return formatTz(date, 'YYYY/MM/DD HH:mm')
  }

  const getStatusTag = (status) => {
    const statusConfig = {
      pending: {
        variant: 'warning',
        text: '待審核',
        icon: <ClockCircleOutlined />
      },
      approved: {
        variant: 'success',
        text: '已通過',
        icon: <CheckCircleOutlined />
      },
      rejected: {
        variant: 'error',
        text: '已拒絕',
        icon: <CloseCircleOutlined />
      }
    }
    const config = statusConfig[status] || { variant: 'default', text: status }
    return (
      <CustomTag variant={config.variant}>
        {config.icon} {config.text}
      </CustomTag>
    )
  }

  const handleTableChange = (newPagination) => {
    setPagination({
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    })
  }

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value
    }))
    setPagination((prev) => ({ ...prev, current: 1 }))
  }

  const handleResetFilters = () => {
    setFilters({
      status: undefined,
      lineId: '',
      purchaseOrderNo: ''
    })
    setPagination((prev) => ({ ...prev, current: 1 }))
  }

  const handleViewDetail = async (record) => {
    try {
      const currentToken =
        token || sessionStorage.getItem('subscription_view_token')
      const response = await api.get(
        `/api/subscription/applications/${record._id}`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`
          }
        }
      )

      if (response.data.success) {
        setSelectedApplication(response.data.data)
        setDetailModalVisible(true)
      }
    } catch (error) {
      message.error('獲取申請詳情失敗')
    }
  }

  const handleUpdateStatus = async (id, status, note) => {
    try {
      const currentToken =
        token || sessionStorage.getItem('subscription_view_token')
      await api.patch(
        `/api/subscription/applications/${id}/status`,
        { status, note },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`
          }
        }
      )
      message.success('狀態更新成功')
      setDetailModalVisible(false)
      fetchApplications()
    } catch (error) {
      message.error(error.response?.data?.message || '狀態更新失敗')
    }
  }

  const handleLogout = () => {
    sessionStorage.removeItem('subscription_view_token')
    setToken(null)
    navigate('/subscription/login')
  }

  const columns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: 80
    },
    {
      title: '電話',
      dataIndex: 'phone',
      key: 'phone',
      width: 120
    },
    {
      title: 'LineID',
      dataIndex: 'lineId',
      key: 'lineId',
      width: 150,
      ellipsis: true
    },
    {
      title: '店名',
      dataIndex: 'storeName',
      key: 'storeName',
      width: 150,
      ellipsis: true
    },
    {
      title: '地區',
      dataIndex: 'region',
      key: 'region',
      width: 100
    },
    {
      title: '購買訂單號',
      dataIndex: 'purchaseOrderNo',
      key: 'purchaseOrderNo',
      width: 200,
      ellipsis: true
    },
    {
      title: '訂閱日期',
      dataIndex: 'subscriptionDate',
      key: 'subscriptionDate',
      width: 120,
      render: (text) => formatDate(text)
    },
    {
      title: '預計開通日期',
      dataIndex: 'expectedActivationDate',
      key: 'expectedActivationDate',
      width: 120,
      render: (text) => (text ? formatDate(text) : '-')
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (status) => getStatusTag(status)
    },
    {
      title: '建立時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (text) => formatDate(text)
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Button
          type='link'
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record)}
          style={{ color: '#a8b5c0' }}
        >
          查看
        </Button>
      )
    }
  ]

  return (
    <PageContainer>
      <ContentWrapper>
        <Header>
          <Title>
            <FileTextOutlined /> 訂閱申請表單管理
          </Title>
          <ActionButtons>
            <StyledButton
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/subscription/list')}
            >
              返回列表
            </StyledButton>
            <StyledButton
              icon={<ReloadOutlined />}
              onClick={fetchApplications}
              loading={loading}
            >
              重新整理
            </StyledButton>
            <StyledButton
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              danger
            >
              登出
            </StyledButton>
          </ActionButtons>
        </Header>

        {/* 篩選器 */}
        <FilterCard>
          <FilterRow>
            <FilterItem>
              <label
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 500
                }}
              >
                搜尋 LineID
              </label>
              <Input
                placeholder='搜尋 LineID'
                value={filters.lineId}
                onChange={(e) => handleFilterChange('lineId', e.target.value)}
                prefix={<SearchOutlined />}
                allowClear
                style={{ borderRadius: '12px', borderColor: '#d4c4b0' }}
              />
            </FilterItem>
            <FilterItem>
              <label
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 500
                }}
              >
                搜尋訂單號
              </label>
              <Input
                placeholder='搜尋購買訂單號'
                value={filters.purchaseOrderNo}
                onChange={(e) =>
                  handleFilterChange('purchaseOrderNo', e.target.value)
                }
                prefix={<SearchOutlined />}
                allowClear
                style={{ borderRadius: '12px', borderColor: '#d4c4b0' }}
              />
            </FilterItem>
            <FilterItem>
              <label
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 500
                }}
              >
                狀態
              </label>
              <Select
                placeholder='選擇狀態'
                value={filters.status}
                onChange={(value) => handleFilterChange('status', value)}
                allowClear
                style={{ width: '100%', borderRadius: '12px' }}
              >
                <Select.Option value='pending'>待審核</Select.Option>
                <Select.Option value='approved'>已通過</Select.Option>
                <Select.Option value='rejected'>已拒絕</Select.Option>
              </Select>
            </FilterItem>
            <FilterItem>
              <Button
                onClick={handleResetFilters}
                style={{ width: '100%', borderRadius: '12px' }}
              >
                重置篩選
              </Button>
            </FilterItem>
          </FilterRow>
        </FilterCard>

        {/* 申請列表 */}
        {loading && applications.length === 0 ? (
          <LoadingContainer>
            <Spin size='large' />
          </LoadingContainer>
        ) : (
          <StyledTable
            columns={columns}
            dataSource={applications}
            rowKey='_id'
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 筆記錄`,
              pageSizeOptions: ['10', '20', '50', '100']
            }}
            onChange={handleTableChange}
            scroll={{ x: 'max-content' }}
          />
        )}

        {/* 詳情 Modal */}
        <Modal
          title='申請表單詳情'
          open={detailModalVisible}
          onCancel={() => setDetailModalVisible(false)}
          footer={null}
          width={800}
          style={{ top: 20 }}
        >
          {selectedApplication && (
            <div>
              <Card
                title='基本資訊'
                style={{ marginBottom: 16, borderRadius: '12px' }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '16px'
                  }}
                >
                  <div>
                    <strong>姓名：</strong>
                    {selectedApplication.name}
                  </div>
                  <div>
                    <strong>電話：</strong>
                    {selectedApplication.phone}
                  </div>
                  <div>
                    <strong>帳號：</strong>
                    {selectedApplication.account}
                  </div>
                  <div>
                    <strong>LineID：</strong>
                    {selectedApplication.lineId}
                  </div>
                  <div>
                    <strong>地區：</strong>
                    {selectedApplication.region}
                  </div>
                  <div>
                    <strong>店名：</strong>
                    {selectedApplication.storeName}
                  </div>
                  <div>
                    <strong>店家類型：</strong>
                    {selectedApplication.storeType}
                  </div>
                  <div>
                    <strong>IG 或網站：</strong>
                    {selectedApplication.igOrWebsite || '-'}
                  </div>
                  <div>
                    <strong>千金序號：</strong>
                    {selectedApplication.qianjinSerialNumber || '-'}
                  </div>
                  <div>
                    <strong>購買訂單號：</strong>
                    {selectedApplication.purchaseOrderNo}
                  </div>
                  <div>
                    <strong>訂閱日期：</strong>
                    {formatDate(selectedApplication.subscriptionDate)}
                  </div>
                  <div>
                    <strong>預計開通日期：</strong>
                    {selectedApplication.expectedActivationDate
                      ? formatDate(selectedApplication.expectedActivationDate)
                      : '-'}
                  </div>
                  <div>
                    <strong>狀態：</strong>
                    {getStatusTag(selectedApplication.status)}
                  </div>
                </div>
                {selectedApplication.note && (
                  <div style={{ marginTop: 16 }}>
                    <strong>備註：</strong>
                    <p style={{ marginTop: 8, color: '#8a8a8a' }}>
                      {selectedApplication.note}
                    </p>
                  </div>
                )}
                <div
                  style={{ marginTop: 16, fontSize: '12px', color: '#8a8a8a' }}
                >
                  <div>
                    建立時間：{formatDate(selectedApplication.createdAt)}
                  </div>
                  <div>
                    更新時間：{formatDate(selectedApplication.updatedAt)}
                  </div>
                </div>
              </Card>

              {selectedApplication.status === 'pending' && (
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                  <Button
                    onClick={() =>
                      handleUpdateStatus(
                        selectedApplication._id,
                        'approved',
                        ''
                      )
                    }
                    type='primary'
                    style={{
                      background:
                        'linear-gradient(135deg, #a8b5c0 0%, #9ca3af 100%)',
                      border: 'none'
                    }}
                  >
                    通過
                  </Button>
                  <Button
                    onClick={() =>
                      handleUpdateStatus(
                        selectedApplication._id,
                        'rejected',
                        ''
                      )
                    }
                    danger
                  >
                    拒絕
                  </Button>
                </Space>
              )}
              <div
                style={{
                  marginTop: 16,
                  display: 'flex',
                  justifyContent: 'flex-end'
                }}
              >
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={() => setDetailModalVisible(false)}
                >
                  返回列表
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </ContentWrapper>
    </PageContainer>
  )
}

export default SubscriptionApplicationsPage
